import axios from 'axios';
import { FormikProvider, useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useParams } from 'react-router-dom';
import { datadogLogs } from '@datadog/browser-logs'

import { backendRoutes, frontendRoutes } from '../../../Constants/constants';
import { phoneValidationSchema } from '../../../Constants/validations';
import Loader from '../../Loader/Loader';
import InputMask from 'react-input-mask';
import { Button } from '../../Button/Button';
import { getBuilderId, getPhoneNumber } from '../../../redux/action/action';
import { honeypot } from '@honeypot-run/core';
import { checkMultiplyPhoneVerification } from '../../../utils';
import { SurveyNavigationState } from '../../../types/navigation';


const PhoneVerification = () => {
	const history = useHistory();
	const locationState = history.location.state as SurveyNavigationState | undefined;
	const { otpProofExpired, ...navigationState } = locationState ?? {};
	const dispatch = useDispatch();
	const builderId = useSelector((state: any) => state.builder.builder_id);
	const previousPath = () => {
		history.goBack();
	}
	let params = useParams<{ builder_name?: string }>();
	const [showAlert, setShowAlert] = useState(!!otpProofExpired);
	const [message, setMessage] = useState(
		otpProofExpired ? 'Your phone verification expired. Please verify your phone number again.' : '',
	);
	const [retryAfter, setRetryAfter] = useState(0);
	const [isLoading, setIsLoading] = useState(false)
	const phoneNumber = useSelector((state: any) => state.phNumber.phNumber?.phone)

	useEffect(() => {
		if (!builderId) {
		dispatch(getBuilderId());
		}
	}, [dispatch, builderId]);

	useEffect(() => {
		if (retryAfter <= 0) return;
		const timer = setTimeout(() => setRetryAfter(s => s - 1), 1000);
		return () => clearTimeout(timer);
	}, [retryAfter]);

	const getOtpCode = (formData: any) => {
		setIsLoading(true);
		axios({
			baseURL: backendRoutes.MOBILE_BASE_URL,
			url: backendRoutes.get_otp,
			method: "post",
			data: formData
		}).then((res) => {
			const secure_token = res.data.secret
			if (res.status === 200 || res.status === 201) {
				honeypot.track(
					'OTP CHECK - Sent Code',
					{ phone: formData.phone });
				setIsLoading(false);
				localStorage.setItem("secure_token", secure_token)
				history.push(frontendRoutes.otpVerify(params.builder_name).path, navigationState)
			}

		}).catch((err) => {
			setIsLoading(false);
			datadogLogs.logger.warn(`Failed to send 2fa code to ${formData.phone}`);
			if (err?.response?.status === 429) {
				const seconds = err.response?.data?.retryAfter ?? 60;
				setRetryAfter(seconds);
				setMessage(`Too many attempts. Try again in ${seconds}s.`);
			} else {
				setMessage('Could not send the code. Please try again.');
			}
			setShowAlert(true);
			setTimeout(() => setShowAlert(false), 3000);
		})
	}

	const formik = useFormik({
		initialValues: {
			phoneNumber: phoneNumber ? phoneNumber : ''
		},
		validationSchema: phoneValidationSchema,
		onSubmit: (values) => {
			const data = {
				phone: values.phoneNumber
			}
			getOtpCode(data)
			dispatch(getPhoneNumber(data));

			checkMultiplyPhoneVerification(values.phoneNumber);

			datadogLogs.logger.info(`Sent 2fa code to phone number: ${values.phoneNumber}`)
		}
	})

	return (
		<section className="main-section phone-screen d-flex justify-content-center">
			<div className="box">
				<div className="main-title">
					<h1>Phone Verification</h1>
					<span><sup>*</sup>required</span>
				</div>
				<span onClick={previousPath} className="link-back icn icn-back-arrow"></span>
				<div className="box-data d-flex flex-column">
					<Form onSubmit={formik.handleSubmit} className="h-100 d-flex flex-column">
						<FormikProvider value={formik}>
							<Form.Group controlId="phone-number">
								<Form.Label>Please enter your phone number<sup>*</sup></Form.Label>
								<InputMask
									mask="999-999-9999"
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									value={formik.values.phoneNumber}
								>
									{
										() => (
											<Form.Control
												type="text"
												placeholder="000-000-0000"
												name="phoneNumber"
												isInvalid={!!formik.errors.phoneNumber}
											/>
										)}</InputMask>
								{/* disabled={phoneNumber ? true : false} */}
								<Form.Control.Feedback type="invalid">
									{formik.errors.phoneNumber}
								</Form.Control.Feedback>
							</Form.Group>
						</FormikProvider>
						{formik.errors.phoneNumber && <div className='links-section d-flex justify-content-end gap-2 mt-2'>
							<a href="tel:+1-844-531-1283" className="d-flex align-items-center gap-1" target="_blank" rel="noreferrer">
								Need Help?
								<i className="icn icn-phone"></i>
							</a>
							or
							<Link to={{ pathname: frontendRoutes.signIn(params.builder_name).path, state: navigationState }}>Login</Link>
						</div> }
						{showAlert && <span className="invalid-feedback d-block">{message}</span>}
						{isLoading && <Loader />}
						<div className="box-footer mt-auto">
              <Button type="submit" className="button w-100" disabled={isLoading || retryAfter > 0}>
                {retryAfter > 0 ? `Try again in ${retryAfter}s` : 'Next'}
              </Button>
            </div>
					</Form>
				</div>
			</div>
		</section>
	)
}

export default PhoneVerification;
